import { useState, type ReactNode } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import {
  ComboBox,
  type ComboBoxOption,
  type ComboBoxProps,
} from './ComboBox/ComboBox';

export interface AsyncComboBoxProps<TParams, TData> extends Omit<
  ComboBoxProps,
  'options'
> {
  useHook: (params: TParams) => { data?: TData };
  baseParams?: TParams;
  mapOptions: (data?: TData) => ComboBoxOption[];
  onAddNew?: () => void;
  none?: boolean;
  labelButton?: ReactNode;
}

/**
 * A combobox component that fetches data asynchronously when the user searches for items.
 * It uses the `useHook` hook to fetch data and the `useDebounce` hook to debounce the search.
 * The component will render a list of options based on the fetched data.
 * The user can select multiple items if the `multiple` prop is set to `true`.
 * The component will also render a search input field where the user can search for items.
 * The component will render an empty message if there are no items in the list.
 */
export function AsyncComboBox<TParams extends object | undefined, TData>({
  width,
  useHook,
  mapOptions,
  baseParams,
  onChange,
  none = true,
  ...props
}: AsyncComboBoxProps<TParams, TData>) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);

  // Build API params dynamically
  const params: TParams = {
    ...(baseParams as TParams),
    ...(debouncedSearch
      ? ({ name: debouncedSearch } as unknown as Partial<TParams>)
      : {}),
  };

  const { data } = useHook(params);
  const options = mapOptions(data);

  // Cast to ComboBoxProps to satisfy the discriminated union type
  const comboBoxProps = {
    ...props,
    onChange,
    width,
    options,
    onSearch: setSearch,
    none,
  } as ComboBoxProps;

  return <ComboBox {...comboBoxProps} />;
}
